// M5.8: admin page for a single team's reward catalog.
// Lives at /admin/teams/:teamId/rewards. Lists the team's
// rewards (open + recently-claimed), provides a create form,
// and a per-reward edit/archive/claim panel.
//
// Per ADR-0015, rewards are team-scoped (no global catalog).
// The page is reached from the team's row in /admin/teams —
// we don't put it in the top-level admin nav because most
// teams won't use coins at all and a top-level link would
// be noise. The link is one click away from where you'd
// expect it (the team panel).

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ApiError } from '../../../lib/api';
import {
  listTeamRewards,
  createTeamReward,
  updateTeamReward,
  claimTeamReward,
  archiveTeamReward,
  listRewardContributions,
  voidRewardContribution,
  type CreateRewardBody,
  type UpdateRewardBody,
} from './api';
import type { TeamReward } from '../../coins/types';

export function TeamRewardsAdminPage() {
  const { teamId } = useParams<{ teamId: string }>();
  if (!teamId) {
    return <p className="text-sm text-err">Missing teamId in URL.</p>;
  }
  const qc = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const rewards = useQuery({
    queryKey: ['admin-team-rewards', teamId, includeArchived],
    queryFn: () => listTeamRewards(teamId, includeArchived),
  });
  const createMut = useMutation({
    mutationFn: (body: CreateRewardBody) => createTeamReward(teamId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-team-rewards', teamId] }),
  });
  const claimMut = useMutation({
    mutationFn: (id: string) => claimTeamReward(teamId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-team-rewards', teamId] }),
  });
  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveTeamReward(teamId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-team-rewards', teamId] }),
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Team rewards</h1>
          <p className="text-sm text-ink-muted">
            Catalog of real-life rewards for this team. Players contribute coins; the team claims when the pool is full.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin/teams" className="text-sm text-ink-muted underline">
            ← Back to teams
          </Link>
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>
      </div>
      <CreateForm onSubmit={(b) => createMut.mutate(b)} error={createMut.error} isPending={createMut.isPending} />
      {rewards.isPending ? (
        <p className="text-sm text-ink-muted">Loading…</p>
      ) : rewards.isError ? (
        <p className="text-sm text-err">Failed to load rewards.</p>
      ) : (rewards.data ?? []).length === 0 ? (
        <p className="text-sm text-ink-muted">
          No rewards yet. Use the form above to add the first one.
        </p>
      ) : (
        <ul className="space-y-3">
          {(rewards.data ?? []).map((r) => (
            <RewardAdminRow
              key={r.id}
              reward={r}
              teamId={teamId}
              onClaim={() => claimMut.mutate(r.id)}
              onArchive={() => archiveMut.mutate(r.id)}
              onUpdate={(body) => updateTeamReward(teamId, r.id, body)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateForm({
  onSubmit,
  error,
  isPending,
}: {
  onSubmit: (b: CreateRewardBody) => void;
  error: unknown;
  isPending: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [coinCost, setCoinCost] = useState(50);
  return (
    <form
      className="card grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: name.trim(),
          description: description.trim(),
          image_url: imageUrl.trim() || undefined,
          coin_cost: coinCost,
        });
        setName('');
        setDescription('');
        setImageUrl('');
        setCoinCost(50);
      }}
    >
      <p className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Add a reward</p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label className="label" htmlFor="rw-name">Name</label>
          <input
            id="rw-name"
            className="input"
            required
            minLength={1}
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Team pizza lunch"
          />
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="rw-cost">Coin cost (pool size)</label>
          <input
            id="rw-cost"
            className="input"
            required
            type="number"
            min={1}
            max={1_000_000}
            value={coinCost}
            onChange={(e) => setCoinCost(Number(e.target.value))}
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="label" htmlFor="rw-desc">Description (optional)</label>
          <textarea
            id="rw-desc"
            className="input min-h-[4rem]"
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What the team gets, who orders it, etc."
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="label" htmlFor="rw-img">Image URL (optional)</label>
          <input
            id="rw-img"
            className="input"
            type="url"
            maxLength={500}
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://…"
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <button className="btn-primary" type="submit" disabled={isPending}>
          {isPending ? 'Creating…' : 'Add reward'}
        </button>
        {error instanceof ApiError ? (
          <p className="text-sm text-err" role="alert">{error.message}</p>
        ) : null}
      </div>
    </form>
  );
}

function RewardAdminRow({
  reward,
  teamId,
  onClaim,
  onArchive,
  onUpdate,
}: {
  reward: TeamReward;
  teamId: string;
  onClaim: () => void;
  onArchive: () => void;
  onUpdate: (body: UpdateRewardBody) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(reward.name);
  const [description, setDescription] = useState(reward.description);
  const [coinCost, setCoinCost] = useState(reward.coin_cost);
  return (
    <li className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{reward.name}</h3>
            <StatusBadge status={reward.status} />
          </div>
          {reward.description ? (
            <p className="mt-1 text-sm text-ink-muted">{reward.description}</p>
          ) : null}
          <p className="mt-1 text-xs text-ink-dim">
            <span aria-hidden>🪙</span> {reward.contributed} / {reward.coin_cost} coins
            {' · '}{reward.contributor_count} contributor{reward.contributor_count === 1 ? '' : 's'}
            {reward.status === 'claimed' && reward.claimed_at ? (
              <> · claimed {new Date(reward.claimed_at).toLocaleDateString()}</>
            ) : null}
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-bg-panel">
            <div
              className="h-full bg-gradient-to-r from-accent to-accent-success"
              style={{ width: `${reward.progress_pct}%` }}
            />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 text-sm">
          {reward.status === 'open' && reward.is_ready_to_claim ? (
            <button
              type="button"
              onClick={onClaim}
              className="btn-primary"
            >
              Claim
            </button>
          ) : null}
          {reward.status === 'open' ? (
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="btn-ghost"
            >
              {editing ? 'Cancel' : 'Edit'}
            </button>
          ) : null}
          {reward.status === 'open' ? (
            <button
              type="button"
              onClick={onArchive}
              className="btn-ghost text-ink-muted"
            >
              Archive
            </button>
          ) : null}
        </div>
      </div>
      {editing ? (
        <form
          className="mt-3 grid gap-3 border-t border-white/5 pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            onUpdate({
              name: name.trim(),
              description: description.trim(),
              coin_cost: coinCost,
            });
            setEditing(false);
          }}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <label className="label">Name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                minLength={1}
                maxLength={100}
              />
            </div>
            <div className="space-y-1">
              <label className="label">Coin cost</label>
              <input
                className="input"
                type="number"
                min={1}
                max={1_000_000}
                value={coinCost}
                onChange={(e) => setCoinCost(Number(e.target.value))}
                disabled={reward.status !== 'open'}
                title={reward.status !== 'open' ? 'Can only change cost while open' : ''}
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="label">Description</label>
              <textarea
                className="input min-h-[4rem]"
                maxLength={2000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      ) : null}
      <ContributionsSection
        teamId={teamId}
        rewardId={reward.id}
        rewardName={reward.name}
      />
    </li>
  );
}

function StatusBadge({ status }: { status: TeamReward['status'] }) {
  if (status === 'open') {
    return (
      <span className="rounded bg-accent/20 px-1.5 py-0.5 text-xs text-accent">Open</span>
    );
  }
  if (status === 'claimed') {
    return (
      <span className="rounded bg-accent-success/20 px-1.5 py-0.5 text-xs text-accent-success">
        Claimed
      </span>
    );
  }
  return (
    <span className="rounded bg-bg-elevated px-1.5 py-0.5 text-xs text-ink-muted">
      Archived
    </span>
  );
}

function ContributionsSection({
  teamId,
  rewardId,
  rewardName,
}: {
  teamId: string;
  rewardId: string;
  rewardName: string;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['admin-reward-contributions', teamId, rewardId],
    queryFn: () => listRewardContributions(teamId, rewardId),
  });
  const voidMut = useMutation({
    mutationFn: (contributionId: string) =>
      voidRewardContribution(teamId, rewardId, contributionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-reward-contributions', teamId, rewardId] });
      qc.invalidateQueries({ queryKey: ['admin-team-rewards', teamId] });
    },
  });
  return (
    <details className="mt-3 border-t border-white/5 pt-3 text-sm">
      <summary className="cursor-pointer text-ink-muted">
        Contributions ({(list.data ?? []).length})
      </summary>
      {list.isPending ? (
        <p className="mt-2 text-xs text-ink-muted">Loading…</p>
      ) : (list.data ?? []).length === 0 ? (
        <p className="mt-2 text-xs text-ink-muted">No contributions yet.</p>
      ) : (
        <ul className="mt-2 divide-y divide-white/5">
          {(list.data ?? []).map((c) => (
            <li key={c.id} className="flex items-center justify-between py-2 text-xs">
              <div>
                <span className="font-medium">
                  {c.contributor.display_name}
                </span>
                <span className="text-ink-muted"> @{c.contributor.handle}</span>
                <span className="ml-2 text-ink-dim">
                  {new Date(c.created_at).toLocaleString()}
                </span>
                {c.state_voided_at ? (
                  <span className="ml-2 rounded bg-err/20 px-1.5 py-0.5 text-err">voided</span>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono">{c.amount}</span>
                {!c.state_voided_at ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Void ${c.contributor.display_name}'s contribution of ${c.amount} coins to ${rewardName}? This refunds the coins to their balance.`)) {
                        voidMut.mutate(c.id);
                      }
                    }}
                    className="btn-ghost text-err"
                    disabled={voidMut.isPending}
                  >
                    Void (refund)
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
